// @flow

import React from 'react';
import useSendbirdStateContext from '@sendbird/uikit-react/useSendbirdStateContext';
import sendbirdSelectors from '@sendbird/uikit-react/sendbirdSelectors';
import { useTranslation } from 'react-i18next';
import { Flex, Position, Box, Stack, Text } from '@locus-taxy/neo-ui';

import UserProfileImage from '../UserProfileImage';
import { isOnline } from 'common/modules/chat/utils/ChatUtils';
import TimeUtils from 'common/utils/TimeUtils';
import DateTimeUtils, {
    TimeStyle,
    DateStyle
} from 'common/utils/DateTimeUtils';

const formatTimestampToDate = (ts: number): string => {
    const momentTime = TimeUtils.getMoment(ts);
    if (TimeUtils.isToday(ts)) {
        return DateTimeUtils.format(momentTime, { timeStyle: TimeStyle.SHORT });
    } else {
        return DateTimeUtils.format(momentTime, {
            dateStyle: DateStyle.SMALL
        });
    }
};
type ConversationCardProps = {
    channel: $FixMe
};

const Conversation = ({ channel }: ConversationCardProps) => {
    const { t } = useTranslation('liveView');

    const context = useSendbirdStateContext();
    const sdkInstance = sendbirdSelectors.getSdk(context);
    const currentUserId = sdkInstance?.currentUser?.userId;

    const { members, unreadMessageCount, lastMessage } = channel;

    const [user] = members.filter(({ userId }) => userId !== currentUserId);

    if (!lastMessage) {
        return null;
    }

    const isUserOnline = user && isOnline(user);
    const msg =
        lastMessage.sender.userId === currentUserId
            ? `You: ${lastMessage.message}`
            : lastMessage.message;
    const timestampString = formatTimestampToDate(lastMessage?.createdAt);
    const isAnyMessageUnread = unreadMessageCount > 0;
    return (
        <Box
            backgroundColor="white"
            borderBottomColor="grey.200"
            borderBottomWidth="thin"
            borderBottomStyle="solid"
            sx={{ cursor: 'pointer' }}
        >
            <Flex
                flexDirection="row"
                alignItems="center"
                width="100%"
                padding={3}
            >
                <Flex flex={1}>
                    <Flex marginRight={4}>
                        <Position position="relative" height={40} width={40}>
                            <UserProfileImage
                                url={user?.plainProfileUrl}
                                isOnline={isUserOnline}
                            />
                        </Position>
                    </Flex>
                    <Flex flex={1}>
                        <Stack>
                            <Text
                                fontSize={2}
                                color="grey.800"
                                fontWeight={600}
                                truncated
                                sx={{ maxWidth: '160px' }}
                            >
                                {user?.nickname ||
                                    user?.userId ||
                                    t('riderManagerChat.deactivatedUser')}
                            </Text>
                            <Text
                                fontSize={1}
                                color="grey.400"
                                style={{
                                    wordBreak: 'break-word',
                                    maxWidth: '15rem'
                                    // to max limit the space message can take, and rest should be truncated
                                }}
                                truncated
                            >
                                {msg}
                            </Text>
                        </Stack>
                    </Flex>
                </Flex>
                <Stack flexDirection="column" align="center">
                    <Text color="grey.400" fontSize={1}>
                        {timestampString}
                    </Text>
                    {isAnyMessageUnread && (
                        <Box
                            height="18px"
                            width="18px"
                            borderRadius="50%"
                            backgroundColor="blue.500"
                        >
                            <Flex alignItems="center" justifyContent="center">
                                <Text fontSize={0} color="grey.100">
                                    {unreadMessageCount}
                                </Text>
                            </Flex>
                        </Box>
                    )}
                </Stack>
            </Flex>
        </Box>
    );
};

export default Conversation;
